import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import characterSettingService from './characterSettingService';
import { CharacterSetting, CharacterSettingListParams, CreateCharacterSettingPayload } from './models/characterSetting';

export interface CharacterSettingState {
  loading: boolean;
  data: CharacterSetting[];
  selectedIds: number[];
}

const initialState: CharacterSettingState = {
  loading: false,
  data: [],
  selectedIds: [],
};

export const fetchCharacterSettings = createAsyncThunk(
  'characterSetting/fetchCharacterSettings',
  async (params: CharacterSettingListParams | undefined, thunkAPI) => {
    try {
      const res = await characterSettingService.getList(params);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createCharacterSetting = createAsyncThunk(
  'characterSetting/createCharacterSetting',
  async (payload: CreateCharacterSettingPayload, thunkAPI) => {
    try {
      const res = await characterSettingService.create(payload);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteCharacterSettings = createAsyncThunk('characterSetting/deleteCharacterSettings', async (ids: number[], thunkAPI) => {
  try {
    await characterSettingService.delete(ids);
    return ids;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const setStatusCharacterSetting = createAsyncThunk('characterSetting/setStatus', async (id: number, thunkAPI) => {
  try {
    const res = await characterSettingService.setStatus(id);
    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const updateCharacterSettings = createAsyncThunk(
  'characterSetting/updateCharacterSettings',
  async (payload: CharacterSetting[], thunkAPI) => {
    try {
      const res = await characterSettingService.updateMany(payload);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const characterSettingSlice = createSlice({
  name: 'characterSetting',
  initialState,
  reducers: {
    toggleSelect(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((x) => x !== id);
      } else {
        state.selectedIds = [...state.selectedIds, id];
      }
    },
    clearSelection(state) {
      state.selectedIds = [];
    },
    selectSingle(state, action: PayloadAction<number>) {
      state.selectedIds = [action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCharacterSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCharacterSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || [];
      })
      .addCase(fetchCharacterSettings.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCharacterSetting.fulfilled, (state, action) => {
        if (action.payload) {
          state.data = [...state.data, action.payload];
        }
      })
      .addCase(deleteCharacterSettings.fulfilled, (state, action) => {
        state.selectedIds = [];
      })
      .addCase(setStatusCharacterSetting.fulfilled, (state, action) => {
        const updatedCharacter = action.payload;
        if (updatedCharacter) {
          state.data = state.data.map((item) => {
            if (item.id === updatedCharacter.id) {
              // Update the specific character with the response from backend
              return { ...item, ...updatedCharacter };
            }
            // For other characters, set status to false if backend returns a character with status=true
            if (updatedCharacter.status) {
              return { ...item, status: false };
            }
            return item;
          });
        }
      })
      .addCase(updateCharacterSettings.fulfilled, (state, action) => {
        state.data = action.payload || [];
        state.selectedIds = [];
      });
  },
});

export const characterSettingActions = characterSettingSlice.actions;
export const characterSettingReducer = characterSettingSlice.reducer;

export const selectCharacterSettings = (state: RootState) => state.characterSetting.data;
export const selectCharacterSettingLoading = (state: RootState) => state.characterSetting.loading;
export const selectCharacterSettingSelectedIds = (state: RootState) => state.characterSetting.selectedIds;

export default characterSettingSlice;
